import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client/core";
import { RetryLink } from "@apollo/client/link/retry";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import ApolloLinkTimeout from "apollo-link-timeout";

import { setContext } from "@apollo/client/link/context";
import { auth } from "../keycloak";

import { onError } from "@apollo/client/link/error";
import { Kind, OperationTypeNode } from "graphql/language";
import type { OperationDefinitionNode } from "graphql/language/ast";
import config, { gqlUri, gqlWsUri } from "../config";

import * as Sentry from "@sentry/vue";
import { DEFAULT_TIMEOUT_MS, clientTimeoutLink } from "./clientTimeoutLink";
import { CustomSentryLink } from "./CustomSentryLink";
import { operationUri } from "./operationUri";

export const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: auth.accessToken ? `Bearer ${auth.accessToken}` : "",
    },
  };
});

const httpLink = new HttpLink({
  // ?op=<name> makes each request identifiable in DevTools instead of a wall of identical
  // "ngpapi/" rows. The backend routes on the path and ignores the query string. A caller can add
  // more parameters through the operation context's `urlParams` (the ctx load's trigger, TR-5544),
  // which the Traefik access log then records.
  uri: (operation) => operationUri(gqlUri, operation.operationName, operation.getContext().urlParams),
});

// Retry configuration: up to 3 attempts on network errors, 15s timeout per attempt
const retryLink = new RetryLink({
  delay: {
    initial: 0, // Start immediately
    max: 1000, // Max 1s delay between retries
    jitter: true, // Add randomness to prevent thundering herd
  },
  attempts: {
    max: 3, // Try up to 3 times
    retryIf: (error, operation) => {
      // Never retry mutations — they are not idempotent and can cause
      // lock conflicts or duplicate side effects
      const definition = getMainDefinition(operation.query);
      if (
        definition.kind === Kind.OPERATION_DEFINITION &&
        (definition as OperationDefinitionNode).operation === OperationTypeNode.MUTATION
      ) {
        return false;
      }
      // Retry queries on network errors, but not on GraphQL errors and not on our own timeout:
      // the backend keeps working on a request the browser gave up on, so a retry after a timeout
      // doubles the load exactly when the backend is too busy to answer in time. The timeout link's
      // error carries a numeric `timeout` (its ES5 subclass of Error defeats instanceof).
      return !!error && !error.result && typeof error.timeout !== "number";
    },
  },
});

const timeoutLink = new ApolloLinkTimeout(DEFAULT_TIMEOUT_MS); // per attempt
const timeoutHttpLink = timeoutLink.concat(httpLink);
const retryTimeoutHttpLink = retryLink.concat(timeoutHttpLink);

// Create the subscription websocket link
const wsLink = new WebSocketLink({
  uri: gqlWsUri,
  options: {
    // See https://www.npmjs.com/package/subscriptions-transport-ws#constructorurl-options-websocketimpl
    lazy: true,
    reconnect: true,
    minTimeout: 5000, // default is 1000, which may not be enough
    connectionParams: () => {
      return {
        authorization: auth.accessToken ? `Bearer ${auth.accessToken}` : "",
      };
    },
  },
});

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation === "subscription";
  },
  wsLink,
  ApolloLink.from([authLink, clientTimeoutLink, retryTimeoutHttpLink]),
);

// For debugging purposes (commented out to not spam the console)
const roundTripLink = new ApolloLink((operation, forward) => {
  // Called before operation is sent to server
  operation.setContext({ start: new Date() });
  // console.debug(`[ApolloClient] Operation ${operation.operationName} started`);

  const observable = forward(operation);
  // WebSocket subscriptions return an Observable without .map(), so guard against that
  if (typeof observable.map === "function") {
    return observable.map((data) => {
      // Called after server responds
      // const time = new Date() - operation.getContext().start;
      // console.debug(`[ApolloClient] Operation ${operation.operationName} took ${time} ms to complete`);
      return data;
    });
  }
  return observable;
});

const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  // A caller that sets `expectedError` in the operation context has a GraphQLError as a NORMAL answer
  // (backupBoostInfo on a legacy-billed service is the case this exists for). Sentry.setTag writes the
  // ISOLATION scope, which in the browser lives for the whole page session, so tagging such an answer
  // would mark essentially every session and every unrelated error after it - the tag stops
  // discriminating the failures it exists for. Log it, never tag it. A network error is never expected.
  const expectedError = operation.getContext().expectedError === true && !!graphQLErrors;

  if (!expectedError) {
    Sentry.setTag("apollo-error", true);
  }

  if (graphQLErrors) {
    const log = expectedError ? console.debug : console.warn;
    graphQLErrors.forEach(function ({ message, locations, path }) {
      log(
        `[GraphQL error] in operation ${operation.operationName}: ${message},\nLocation: ${locations},\nPath: ${path}`,
      );
    });
  } else if (networkError) {
    console.warn(`[Network error] in operation ${operation.operationName}: ${networkError}`);
  } else {
    console.warn(`[Unknown error] in operation ${operation.operationName}`);
  }

  // Automagically handle errors by creating a notification if requested through the context, or it is a network error on a mutation
  const isMutation = !!operation.query.definitions.find(
    (definition) =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      (definition as OperationDefinitionNode).operation === OperationTypeNode.MUTATION,
  );
  const autoError = operation.getContext().autoError;
  if (autoError || (isMutation && networkError)) {
    if (autoError) console.debug("Automatically handling error with a notification on request of the caller");
  }
});

const defaultOptions = {
  watchQuery: {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

let link;
if (config.sentry) {
  const sentryLink = new CustomSentryLink({
    uri: gqlUri,
    setFingerprint: false,
    setTransaction: false,
    attachBreadcrumbs: {
      includeQuery: true,
      includeVariables: true,
      includeFetchResult: false,
      includeError: true,
    },
  });
  link = ApolloLink.from([errorLink, roundTripLink, sentryLink, splitLink]);
} else {
  link = ApolloLink.from([errorLink, roundTripLink, splitLink]);
}

// Create the apollo client
export default new ApolloClient({
  link,
  cache: new InMemoryCache(),
  connectToDevTools: true,
  defaultOptions: defaultOptions,
});
